import { Canvas, FabricObject, Path } from 'fabric';
import { buildArrowPath } from 'shared/fabric-drawing/tools';

/**
 * Awareness field carrying the writer's live highlight-cursor position (`canvas-highlight-cursor`).
 * Ephemeral: streamed over `y-protocols/awareness`, never written to the Y.Doc (no clock advance,
 * no tombstones, no undo/export). Distinct from the reserved `drawingStroke` field (in-progress
 * freehand preview) — a pointer and a stroke are different gestures.
 */
export const HIGHLIGHT_CURSOR_FIELD = 'highlightCursor';

/** The ephemeral payload published per pointer move; `null` clears the cursor. */
export interface HighlightCursorPayload {
    /** Cursor tip X in logical scene space (1280×720) — panorama-pan-independent. */
    x: number;
    /** Cursor tip Y in logical scene space (1280×720). */
    y: number;
    /** Arrow color — the writer's currently-selected drawing color. */
    color: string;
}

// Fixed LOGICAL geometry: the arrow scales with the canvas contain-fit like every other object,
// so it looks the same on the teacher and a differently-sized beamer. Tip sits on the cursor;
// the shaft runs to the lower-right, so the arrow points up-left (matching the legacy SVG pointer).
const SHAFT_LENGTH = 46;
const DIAGONAL_OFFSET = SHAFT_LENGTH / Math.SQRT2; // 45° component toward lower-right
const STROKE_WIDTH = 6;
const HEAD_LENGTH = 24;

/** No real awareness clientId is negative, so -1 safely keys the local (writer's own) arrow. */
const LOCAL_KEY = -1;

function isHighlightCursorPayload(value: unknown): value is HighlightCursorPayload {
    if (value === null || typeof value !== 'object') return false;
    const p = value as Partial<HighlightCursorPayload>;
    // Number.isFinite (not `typeof === 'number'`): reject NaN/±Infinity so a malformed peer
    // payload can't produce a Path with degenerate geometry.
    return Number.isFinite(p.x) && Number.isFinite(p.y) && typeof p.color === 'string';
}

/**
 * Owns the transient laser-pointer arrow(s) on one fabric canvas — the right-mouse highlight
 * cursor (`canvas-highlight-cursor`). One arrow is tracked per source: the local writer
 * ({@link LOCAL_KEY}) and each remote awareness client (keyed by clientId).
 *
 * Every arrow is:
 *  - `_yjsEphemeral` → the sync binding skips it on add/modify/remove, so it never reaches the
 *    Y.Doc, undo history, or a favorites export;
 *  - `excludeFromExport` → also dropped by `snapshotDrawingObjects` / fabric `toObject()`;
 *  - non-selectable / non-evented → cannot be picked and never interferes with drawing/hit-testing.
 *
 * A Path is rebuilt on each move rather than mutated in place (same rationale as
 * `ShapeDrawingController.updateArrow` — an arrow Path is awkward to mutate).
 */
export class HighlightCursorController {
    // key → arrow Path currently on the canvas (LOCAL_KEY = writer's own; positive = remote clientId).
    // NOTE: a `syncFromYjs` reconcile calls `canvas.clear()`, removing these arrows from the canvas
    // while the Map still references them. This self-heals with no leak: `place()` calls `remove()`
    // first (fabric `canvas.remove` on an already-removed object is a no-op) then re-adds, and
    // `remove()`/`dispose()` tolerate already-removed objects. A mid-gesture reconcile (rare —
    // reconnect / initial load) just drops the arrow until the next pointer/awareness update.
    private readonly arrows = new Map<number, Path>();

    constructor(private readonly canvas: Canvas) {}

    /** Show/move the writer's own cursor immediately (no round-trip). */
    showLocal(x: number, y: number, color: string): void {
        this.place(LOCAL_KEY, x, y, color);
        this.canvas.requestRenderAll();
    }

    /** Remove the writer's own cursor (right-button release / pointer leave). */
    clearLocal(): void {
        if (this.remove(LOCAL_KEY)) this.canvas.requestRenderAll();
    }

    /**
     * Apply a remote client's ephemeral payload — shaped as an `EphemeralApplyHandler`:
     * returns `true` when the canvas needs a re-render (the binding's awareness listener
     * batches the actual `renderAll`). `null` (clear / client departed) removes the arrow.
     */
    applyRemote(payload: unknown | null, clientId: number): boolean {
        if (payload === null) return this.remove(clientId);
        if (!isHighlightCursorPayload(payload)) return false;
        this.place(clientId, payload.x, payload.y, payload.color);
        return true;
    }

    /** Remove every cursor (renderer teardown / slide change). */
    dispose(): void {
        let changed = false;
        for (const key of [...this.arrows.keys()]) changed = this.remove(key) || changed;
        if (changed) this.canvas.requestRenderAll();
    }

    private place(key: number, x: number, y: number, color: string): void {
        this.remove(key);
        const arrow = new Path(
            // Tail at the lower-right, tip (arrowhead) on the cursor point.
            buildArrowPath(x + DIAGONAL_OFFSET, y + DIAGONAL_OFFSET, x, y, STROKE_WIDTH, { headLength: HEAD_LENGTH }),
            {
                stroke: color,
                strokeWidth: STROKE_WIDTH,
                fill: '',
                strokeLineCap: 'round',
                strokeLineJoin: 'round',
                selectable: false,
                evented: false,
                excludeFromExport: true,
                objectCaching: false,
            },
        );
        (arrow as FabricObject & { _yjsEphemeral?: boolean })._yjsEphemeral = true; // set BEFORE add → binding skips it
        this.arrows.set(key, arrow);
        this.canvas.add(arrow);
        this.canvas.bringObjectToFront(arrow);
    }

    private remove(key: number): boolean {
        const arrow = this.arrows.get(key);
        if (!arrow) return false;
        this.canvas.remove(arrow);
        this.arrows.delete(key);
        return true;
    }
}
